
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: string;
}

const ACHIEVEMENTS: Omit<Achievement, 'unlocked' | 'unlockedAt'>[] = [
  {
    id: 'first-login',
    title: 'Welcome Aboard',
    description: 'Successfully logged into CSO Agent',
    icon: '🎉'
  },
  {
    id: 'business-assessment',
    title: 'Strategic Thinker',
    description: 'Completed your business assessment',
    icon: '🎯'
  },
  {
    id: 'first-chat',
    title: 'AI Strategist',
    description: 'Had your first conversation with the CSO Agent',
    icon: '💬'
  },
  {
    id: 'first-document',
    title: 'Document Creator',
    description: 'Generated your first strategic document',
    icon: '📄'
  },
  {
    id: 'business-plan',
    title: 'Master Planner',
    description: 'Created a comprehensive business plan',
    icon: '📊'
  },
  {
    id: 'three-documents',
    title: 'Productive Strategist',
    description: 'Created 3 strategic documents',
    icon: '🏆'
  }
];

export function useAchievements() {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [newlyUnlocked, setNewlyUnlocked] = useState<Achievement[]>([]);

  useEffect(() => {
    checkAchievements();
  }, []);

  const checkAchievements = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const userId = session.user.id;
      const unlockedAchievements: Achievement[] = [];

      // Get stored achievements from localStorage
      const storedAchievements = localStorage.getItem(`achievements_${userId}`);
      const previouslyUnlocked = storedAchievements ? JSON.parse(storedAchievements) : [];

      // Check first login (always unlocked if logged in)
      unlockedAchievements.push({
        ...ACHIEVEMENTS.find(a => a.id === 'first-login')!,
        unlocked: true,
        unlockedAt: session.user.created_at
      });

      // Check business assessment
      const { data: assessmentData } = await supabase
        .from('onboarding_responses')
        .select('created_at')
        .eq('user_id', userId)
        .limit(1);

      if (assessmentData && assessmentData.length > 0) {
        unlockedAchievements.push({
          ...ACHIEVEMENTS.find(a => a.id === 'business-assessment')!,
          unlocked: true,
          unlockedAt: assessmentData[0].created_at
        });
      }

      // Check first chat
      const { data: chatData } = await supabase
        .from('chat_sessions')
        .select('created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: true })
        .limit(1);

      if (chatData && chatData.length > 0) {
        unlockedAchievements.push({
          ...ACHIEVEMENTS.find(a => a.id === 'first-chat')!,
          unlocked: true,
          unlockedAt: chatData[0].created_at
        });
      }

      // Check documents
      const { data: documentData } = await supabase
        .from('documents')
        .select('type, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (documentData && documentData.length > 0) {
        // First document
        unlockedAchievements.push({
          ...ACHIEVEMENTS.find(a => a.id === 'first-document')!,
          unlocked: true,
          unlockedAt: documentData[0].created_at
        });

        // Business plan specific
        const businessPlan = documentData.find(doc => doc.type === 'business_plan');
        if (businessPlan) {
          unlockedAchievements.push({
            ...ACHIEVEMENTS.find(a => a.id === 'business-plan')!,
            unlocked: true,
            unlockedAt: businessPlan.created_at
          });
        }

        // Three documents
        if (documentData.length >= 3) {
          unlockedAchievements.push({
            ...ACHIEVEMENTS.find(a => a.id === 'three-documents')!,
            unlocked: true,
            unlockedAt: documentData[2].created_at
          });
        }
      }

      // Add locked achievements
      const allAchievements = ACHIEVEMENTS.map(achievement => {
        const unlocked = unlockedAchievements.find(ua => ua.id === achievement.id);
        return unlocked || { ...achievement, unlocked: false };
      });

      // Check for newly unlocked achievements
      const newUnlocked = unlockedAchievements.filter(achievement => 
        !previouslyUnlocked.some((prev: Achievement) => prev.id === achievement.id)
      );

      if (newUnlocked.length > 0) {
        setNewlyUnlocked(newUnlocked);
        // Store updated achievements
        localStorage.setItem(`achievements_${userId}`, JSON.stringify(unlockedAchievements));
      }

      setAchievements(allAchievements);
    } catch (error) {
      console.error('Error checking achievements:', error);
    }
  };

  const dismissNewAchievements = () => {
    setNewlyUnlocked([]);
  };

  return {
    achievements,
    newlyUnlocked,
    checkAchievements,
    dismissNewAchievements
  };
}
